import React, { useEffect, useState } from "react";
import { Select, Spin, Typography } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import styles from "./student-form.module.css";
import StudentInformation from "./components/student-information/student-information";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import CustomButton from "../../../../common/custom-button/custom-button";
import StudentLevelClass from "./components/student-level-class/student-level-class";
import StudentContactInformation from "./components/student-contact-information/student-contact-information";
import ParentInformation from "./components/parent-information/parent-information";
import BankInformation from "./components/bank-information/bank-information";
import MedicalInformation from "./components/medical-information/medical-information";
import StudentDeclarationForm from "./components/student-declaration-form/student-declaration-form";
import {
  createStudent,
  fetchStudentDetails,
  updateStudent,
} from "../../../../services/student";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { validateDate } from "../../../../utils/format-date/format-date";
import { useSelector } from "react-redux";
import { checkMixedSchool } from "../../../../utils/common";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import { useAcademicYears } from "../../../../hooks/useAcademicYear";

const validationSchema = {
  form1: yup.object().shape({
    first_name: yup.string().required("First name is required"),
    middle_name: yup.string().notRequired(),
    last_name: yup.string().required("Last name is required"),
    gender: yup.string().required("Gender is required"),
    date_of_birth: yup.string().required("Date of birth is required"),
    nationality: yup.string().notRequired(),
    national_id_number: yup.string().notRequired(),
    profile_picture: yup.mixed().notRequired(),
    birth_certificate: yup.mixed().notRequired(),
    beam_student: yup.string().notRequired(),
  }),
  form2: yup.object().shape({
    level_id: yup.string().required("Level is required"),
    classroom_id: yup.string().required("Class is required"),
  }),
  form3: yup.object().shape({
    email: yup.string().notRequired(),
    phone: yup.string().notRequired(),
    country: yup.string().notRequired(),
    province: yup.string().notRequired(),
    city: yup.string().notRequired(),
    address: yup.string().notRequired(),
    emergency_contact_number: yup.string().notRequired(),
    emergency_contact_person: yup.string().notRequired(),
  }),
  form4: yup.object().shape({
    form4Fields: yup
      .array()
      .of(
        yup.object().shape({
          full_name: yup.string().required("Parent name is required"),
          parent_type: yup.string().required("Relationship is required"),
          email: yup.string().email("Invalid email").notRequired(),
          phone_number: yup.string().required("Phone number is required"),
          occupation: yup.string().notRequired(),
          address: yup.string().notRequired(),
        })
      )
      .min(1, "At least one parent/guardian is required")
      .required("Parent/guardian information is required"),
  }),
  form5: yup.object().shape({
    account_holder_name: yup.string().notRequired(),
    bank_name: yup.string().notRequired(),
    bank_account_number: yup.string().notRequired(),
    branch_code: yup.string().notRequired(),
  }),
  form6: yup.object().shape({
    allergies: yup.string().notRequired(),
    special_needs: yup.string().notRequired(),
  }),
  form7: yup.object().shape({
    declaration: yup
      .boolean()
      .oneOf([true], "You must accept the Declaration")
      .required(),
    consent: yup
      .boolean()
      .oneOf([true], "You must accept the terms and conditions")
      .required(),
    signature: yup.string().required("Signature is required"),
    date: yup.string().required("Date is required"),
    academic_year_id: yup.string().required("Academic year is required"),
  }),
};

const defaultValues = {
  form1: {
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    date_of_birth: "",
    nationality: "",
    national_id_number: "",
    profile_picture: null,
    birth_certificate: null,
    beam_student: "",
    student_institution_type: "",
  },
  form2: {
    level_id: "",
    classroom_id: "",
  },
  form3: {
    email: "",
    phone: "",
    country: "",
    province: "",
    city: "",
    address: "",
    emergency_contact_number: "",
    emergency_contact_person: "",
  },
  form4: {
    form4Fields: [
      {
        id: "",
        full_name: "",
        parent_type: "",
        email: "",
        phone_number: "",
        occupation: "",
        address: "",
      },
    ],
  },
  form5: {
    account_holder_name: "",
    bank_name: "",
    bank_account_number: "",
    branch_code: "",
  },
  form6: {
    allergies: "",
    special_needs: "",
  },
  form7: {
    declaration: false,
    consent: false,
    signature: "",
    date: "",
    academic_year_id: "",
  },
};

const STUDENTS_FORMS = [
  {
    label: "Student Information",
    Component: StudentInformation,
  },
  {
    label: "Level and Class",
    Component: StudentLevelClass,
  },
  {
    label: "Contact Information",
    Component: StudentContactInformation,
  },
  {
    label: "Parent/Guardian Information",
    Component: ParentInformation,
  },
  {
    label: "Bank Information",
    Component: BankInformation,
  },
  {
    label: "Health and Special Needs",
    Component: MedicalInformation,
  },
  {
    label: "Declaration & Consent",
    Component: StudentDeclarationForm,
  },
];

const StudentForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const studentId = params.get("uid");
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.user?.user);
  const isMixedSchool = checkMixedSchool(user);
  const {
    yearList,
    selectedYear: activeAcademicYear,
    onPopupScroll,
  } = useAcademicYears();

  const dynamicForm1Schema = validationSchema.form1.shape({
    student_institution_type: isMixedSchool
      ? yup.string().required("Day / Boarding selection is required")
      : yup.string().notRequired(),
  });
  const form1 = useForm({
    resolver: yupResolver(dynamicForm1Schema),
    defaultValues: defaultValues.form1,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form2 = useForm({
    resolver: yupResolver(validationSchema.form2),
    defaultValues: defaultValues.form2,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form3 = useForm({
    resolver: yupResolver(validationSchema.form3),
    defaultValues: defaultValues.form3,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form4 = useForm({
    resolver: yupResolver(validationSchema.form4),
    defaultValues: defaultValues.form4,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form4DynamicFields = useFieldArray({
    control: form4.control,
    name: "form4Fields",
  });
  const form5 = useForm({
    resolver: yupResolver(validationSchema.form5),
    defaultValues: defaultValues.form5,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form6 = useForm({
    resolver: yupResolver(validationSchema.form6),
    defaultValues: defaultValues.form6,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const form7 = useForm({
    resolver: yupResolver(validationSchema.form7),
    defaultValues: defaultValues.form7,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const FORM_SET = [
    form1,
    form2,
    form3,
    { ...form4, ...form4DynamicFields },
    form5,
    form6,
    form7,
  ];

  useEffect(() => {
    if (studentId) {
      getStudentData();
    } else {
      setStudentDetails(null);
    }
  }, [studentId]);

  useEffect(() => {
    if (activeAcademicYear && !studentId) {
      form7.setValue("academic_year_id", activeAcademicYear);
    }
  }, [activeAcademicYear]);

  const getStudentData = async () => {
    try {
      setLoading(true);
      const response = await fetchStudentDetails(studentId);
      if (response?.data) {
        const data = response?.data?.data;
        setStudentDetails(data);
        populateFormData(data);
      }
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const getImageName = (img) => {
    const imageName = img
      ? decodeURIComponent(img)?.split("#")?.slice(-1)?.[0] ?? null
      : null;
    return imageName ? { name: imageName } : {};
  };

  const populateFormData = (data) => {
    form1.reset({
      first_name: data?.first_name ?? "",
      middle_name: data?.middle_name ?? "",
      last_name: data?.last_name ?? "",
      gender: data?.gender ?? "",
      date_of_birth: validateDate(data?.date_of_birth),
      nationality: data?.nationality ?? "",
      national_id_number: data?.national_id_number ?? "",
      profile_picture: getImageName(data?.profile_picture),
      birth_certificate: getImageName(data?.birth_certificate),
      beam_student: typeof data?.is_beam === "boolean" 
        ? (data.is_beam ? "Yes" : "No") 
        : "",
      student_institution_type: data?.student_institution_type ?? "",
    });

    form2.reset({
      level_id: data?.classroom?.level_id ?? "",
      classroom_id: data?.classroom_id ?? "",
    });

    form3.reset({
      email: data?.contact_details?.email ?? "",
      phone: data?.contact_details?.phone ?? "",
      address: data?.contact_details?.address ?? "",
      city: data?.contact_details?.city ?? "",
      province: data?.contact_details?.province ?? "",
      country: data?.contact_details?.country ?? "",
      emergency_contact_number:
        data?.contact_details?.emergency_contact_number ?? "",
      emergency_contact_person:
        data?.contact_details?.emergency_contact_person ?? "",
    });

    const rawParents =
      Array.isArray(data?.parents) && data?.parents?.length > 0
        ? data?.parents
        : [{}];

    form4.reset({
      form4Fields: rawParents?.map((i) => ({
        id: i?.id ?? "",
        full_name: i?.full_name,
        parent_type: i?.parent_type,
        email: i?.email,
        phone_number: i?.phone_number,
        occupation: i?.occupation,
        address: i?.address,
      })),
    });

    form5.reset({
      account_holder_name: data?.bank_details?.[0]?.account_holder_name,
      bank_name: data?.bank_details?.[0]?.bank_name,
      bank_account_number: data?.bank_details?.[0]?.bank_account_number,
      branch_code: data?.bank_details?.[0]?.branch_code,
    });

    form6.reset({
      allergies: data?.medical_details?.allergies ?? "",
      special_needs: data?.medical_details?.special_needs ?? "",
    });

    form7.reset({
      signature: data?.metadata?.signature ?? "",
      date: dayjs(data?.metadata?.date).isValid()
        ? dayjs(data?.metadata?.date)
        : "",
      academic_year_id: data?.academic_year_id ?? "",
    });
  };

  const handleSubmitForm = async () => {
    try {
      const form1Values = form1.getValues();
      const form2Values = form2.getValues();
      const form3Values = form3.getValues();
      const form4Values = form4.getValues();
      const form5Values = form5.getValues();
      const form6Values = form6.getValues();
      const form7Values = form7.getValues();

      const formData = new FormData();

      // Form 1
      formData.append("first_name", form1Values.first_name);
      formData.append("middle_name", form1Values.middle_name);
      formData.append("last_name", form1Values.last_name);
      formData.append("gender", form1Values.gender);
      formData.append(
        "date_of_birth",
        dayjs(form1Values.date_of_birth).format("MM-DD-YYYY")
      );
      formData.append("nationality", form1Values.nationality);
      formData.append("national_id_number", form1Values.national_id_number);

      if (form1Values.profile_picture?.size) {
        formData.append("profile_picture", form1Values.profile_picture);
      }
      if (form1Values.birth_certificate?.size) {
        formData.append("birth_certificate", form1Values.birth_certificate);
      }
      const isBeam = form1Values.beam_student === "yes" 
        ? "true" 
        : form1Values.beam_student === "no"
        ? "false"
        : "";
      formData.append("is_beam", isBeam);
      const studentInstitutionType = isMixedSchool
        ? form1Values.student_institution_type
        : user?.institution?.boarding_or_day
      formData.append("student_institution_type", studentInstitutionType);

      // Form 2
      formData.append("academic_details", JSON.stringify(form2Values));
      formData.append("classroom_id", form2Values.classroom_id);

      // Form 3
      formData.append("contact_details", JSON.stringify(form3Values));

      // Form 4
      const parents = form4Values.form4Fields.map((i) => {
        return {
          id: i?.id ?? "",
          full_name: i.full_name,
          parent_type: i.parent_type,
          email: i.email,
          phone_number: i.phone_number,
          occupation: i.occupation,
          address: i.address,
        };
      });
      formData.append("parent_details", JSON.stringify(parents));

      // Form 5
      formData.append("bank_details", JSON.stringify(form5Values));

      // Form 6
      formData.append("medical_details", JSON.stringify(form6Values));

      // Form 7
      const metadata = {
        signature: form7Values.signature,
        date: dayjs(form7Values.date).format("MM-DD-YYYY"),
      };
      formData.append("metadata", JSON.stringify(metadata));
      formData.append("academic_year_id", form7Values.academic_year_id);

      setLoading(true);
      if (studentDetails) {
        await updateStudent(studentDetails?.id, formData);
      } else {
        await createStudent(formData);
      }
      toast.success(
        `Student is ${studentDetails ? "updated" : "added"} successfully!`
      );
      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || "Failed to add student");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    try {
      const results = await Promise.all([
        form1.trigger(),
        form2.trigger(),
        form3.trigger(),
        form4.trigger(),
        form5.trigger(),
        form6.trigger(),
        form7.trigger(),
      ]);

      const allValid = results.every(Boolean);
      if (allValid) {
        await handleSubmitForm();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>
          {studentId ? "Edit Student" : "Add Student"}
        </Typography>
      </div>
      <div gutter={[12]} className={styles.academicYearRow}>
        <p className={styles.academicYearLabel}>
          Academic Year:
          <span style={{ color: "red" }}>*</span>
        </p>
        <Controller
          name="academic_year_id"
          control={form7.control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <CustomSelect
              className={styles.selectYearField}
              placeholder="Select Year"
              value={value}
              onChange={onChange}
              onPopupScroll={onPopupScroll}
              status={error ? "error" : ""}
            >
              {yearList.map((item, index) => (
                <Select.Option key={item?.id || index} value={item?.id}>
                  {item?.name}
                </Select.Option>
              ))}
            </CustomSelect>
          )}
        />
      </div>

      <div className={styles.studentForm}>
        <div>
          {STUDENTS_FORMS?.map((item, index) => {
            const Component = item?.Component;
            return (
              <div className={styles.formComponentWrapper}>
                <div className={styles.formTitle}>{item?.label}</div>
                <Component {...FORM_SET[index]} />
              </div>
            );
          })}
        </div>
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
        >
          <CustomButton
            onClick={() => navigate(-1)}
            title="Cancel"
            variant="outlined"
            style={{
              width: "95px",
              backgroundColor: "transparent",
              color: "#4A4A4A",
              border: "1px solid #888888",
            }}
          />
          <CustomButton
            onClick={handleAddStudent}
            title={studentId ? "Update Student" : "Add Student"}
            type="submit"
            variant="outlined"
          />
        </div>
      </div>
    </Spin>
  );
};

export default StudentForm;
